/**
 * AddEntityPanel — Modo Edición: añadir un regulador o semáforo nuevo al
 * dataset (ARS-05). Guardado directo desde 2026-09-10 — al confirmar, el
 * alta ya está persistida y visible para todos los operadores.
 *
 * Vive fuera de `RegulatorEditPanel` a propósito: añadir no requiere tener
 * un regulador seleccionado.
 */
import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus } from 'lucide-react';
import { useEditMode } from '../../store/editMode';
import { useRegulatorLifecycleDraft } from '../../store/regulatorLifecycleDraft';
import { useSignalLifecycleDraft } from '../../store/signalLifecycleDraft';
import { useOverridesVersion } from '../../store/overridesVersion';
import { addedRegulators } from '../../data/overridesRuntime';
import { santaColomaIntersections } from '../../data/santa-coloma';
import { flyToPoint } from '../../utils/camera-api';

function fmt(n: number): string {
  return n.toFixed(6);
}

export function AddEntityPanel() {
  useOverridesVersion(s => s.version); // re-render al guardar (ver overridesRuntime.ts)
  const addingRegulator = useEditMode(s => s.addingRegulator);
  const stopAddingRegulator = useEditMode(s => s.stopAddingRegulator);
  const pendingNewRegulatorCoord = useEditMode(s => s.pendingNewRegulatorCoord);
  const clearPendingNewRegulatorCoord = useEditMode(s => s.clearPendingNewRegulatorCoord);

  const addingSignal = useEditMode(s => s.addingSignal);
  const stopAddingSignal = useEditMode(s => s.stopAddingSignal);
  const pendingNewSignalCoord = useEditMode(s => s.pendingNewSignalCoord);
  const clearPendingNewSignalCoord = useEditMode(s => s.clearPendingNewSignalCoord);

  const stageAddRegulator = useRegulatorLifecycleDraft(s => s.stageAdd);
  const regulatorSaving = useRegulatorLifecycleDraft(s => s.saving);
  const stageAddSignal = useSignalLifecycleDraft(s => s.stageAdd);
  const signalSaving = useSignalLifecycleDraft(s => s.saving);

  const [idInput, setIdInput] = useState('');
  const [idError, setIdError] = useState('');

  // Esc cancela el modo picking activo — no puede reusar el listener de
  // RegulatorEditPanel porque este panel vive sin selección previa.
  useEffect(() => {
    if (!addingRegulator && !addingSignal) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      stopAddingRegulator();
      stopAddingSignal();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [addingRegulator, addingSignal, stopAddingRegulator, stopAddingSignal]);

  const submitRegulator = async () => {
    const id = parseInt(idInput, 10);
    if (!Number.isInteger(id) || id <= 0) {
      setIdError('ID inválido — usa un número entero positivo');
      return;
    }
    const exists = santaColomaIntersections.some(ix => ix.cabinet.logicalId === id)
      || addedRegulators.some(d => d.logicalId === id);
    if (exists) {
      setIdError(`Ya existe un regulador con ID ${id}`);
      return;
    }
    if (!pendingNewRegulatorCoord) return;
    if (!(await stageAddRegulator({ logicalId: id, position: pendingNewRegulatorCoord }))) {
      setIdError('No se pudo guardar — reintenta');
      return;
    }
    flyToPoint(pendingNewRegulatorCoord);
    setIdInput('');
    setIdError('');
    clearPendingNewRegulatorCoord();
  };

  const submitSignal = async () => {
    if (!pendingNewSignalCoord) return;
    if (!(await stageAddSignal(pendingNewSignalCoord))) return;
    flyToPoint(pendingNewSignalCoord);
    clearPendingNewSignalCoord();
  };

  const nothingToShow = !addingRegulator && !pendingNewRegulatorCoord && !addingSignal && !pendingNewSignalCoord;

  return (
    <AnimatePresence>
      {!nothingToShow && (
        <motion.div
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -20, opacity: 0 }}
          transition={{ duration: 0.15 }}
          className="fixed top-20 left-1/2 -translate-x-1/2 z-40 w-72
                     bg-gray-950/95 backdrop-blur-md border border-amber-700/40 rounded-lg shadow-2xl p-3 space-y-2.5"
        >
          {addingRegulator && (
            <div className="flex items-center justify-between gap-2 text-[11px] text-amber-300">
              <span>Click en el mapa para ubicar el nuevo regulador · Esc cancela</span>
              <button onClick={stopAddingRegulator} className="text-gray-500 hover:text-white flex-shrink-0">✕</button>
            </div>
          )}

          {pendingNewRegulatorCoord && (
            <div className="space-y-2">
              <div className="text-[10px] uppercase tracking-wide text-gray-500 flex items-center gap-1.5">
                <Plus className="w-3 h-3" />Nuevo regulador
              </div>
              <div className="text-[11px] font-mono text-amber-300">
                {fmt(pendingNewRegulatorCoord[1])}, {fmt(pendingNewRegulatorCoord[0])}
              </div>
              <div>
                <label className="text-[10px] text-gray-500">ID (p. ej. 80)</label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={idInput}
                  onChange={e => setIdInput(e.target.value)}
                  className="w-full bg-gray-800 text-gray-100 font-mono text-[11px] px-2 py-1.5 rounded border border-gray-700 focus:border-amber-500 focus:outline-none"
                />
              </div>
              {idError && <div className="text-[11px] text-red-400">{idError}</div>}
              <div className="flex gap-2">
                <button
                  onClick={clearPendingNewRegulatorCoord}
                  className="flex-1 px-3 py-1.5 rounded text-xs font-medium bg-gray-800 text-gray-300 hover:bg-gray-700 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={submitRegulator}
                  disabled={regulatorSaving}
                  className="flex-1 px-3 py-1.5 rounded text-xs font-medium bg-amber-600 text-white hover:bg-amber-500 transition-colors disabled:opacity-50 disabled:cursor-wait"
                >
                  {regulatorSaving ? 'Añadiendo…' : 'Añadir'}
                </button>
              </div>
            </div>
          )}

          {addingSignal && (
            <div className="flex items-center justify-between gap-2 text-[11px] text-amber-300">
              <span>Click en el mapa para ubicar el nuevo semáforo · Esc cancela</span>
              <button onClick={stopAddingSignal} className="text-gray-500 hover:text-white flex-shrink-0">✕</button>
            </div>
          )}

          {pendingNewSignalCoord && (
            <div className="space-y-2">
              <div className="text-[10px] uppercase tracking-wide text-gray-500 flex items-center gap-1.5">
                <Plus className="w-3 h-3" />Nuevo semáforo
              </div>
              <div className="text-[11px] font-mono text-amber-300">
                {fmt(pendingNewSignalCoord[1])}, {fmt(pendingNewSignalCoord[0])}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={clearPendingNewSignalCoord}
                  className="flex-1 px-3 py-1.5 rounded text-xs font-medium bg-gray-800 text-gray-300 hover:bg-gray-700 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={submitSignal}
                  disabled={signalSaving}
                  className="flex-1 px-3 py-1.5 rounded text-xs font-medium bg-amber-600 text-white hover:bg-amber-500 transition-colors disabled:opacity-50 disabled:cursor-wait"
                >
                  {signalSaving ? 'Añadiendo…' : 'Añadir'}
                </button>
              </div>
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
